import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Sparkles, Loader2, PlayCircle } from "lucide-react";
import { PHASES, TOTAL_STEPS, WIZARD_STORAGE_KEY } from "@/lib/wizardConfig";
import { DEMO_STEP_DATA, DEMO_FULL_DATA } from "@/lib/demoData";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function CoCreator() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [generating, setGenerating] = useState(false);
  const [demoRunning, setDemoRunning] = useState(false);

  // Restore draft from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(WIZARD_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.data) setData(parsed.data);
        if (typeof parsed?.step === "number") setStep(parsed.step);
      }
    } catch (e) {
      // ignore corrupted draft
    }
  }, []);

  // Persist draft (skip during demo run so demo doesn't overwrite user's real draft)
  useEffect(() => {
    if (demoRunning) return;
    try {
      localStorage.setItem(
        WIZARD_STORAGE_KEY,
        JSON.stringify({ step, data })
      );
    } catch (e) {
      // ignore quota errors
    }
  }, [step, data, demoRunning]);

  const phase = PHASES[step];

  const isStepValid = useMemo(() => {
    return phase.fields.every((f) => {
      const v = data[f.key];
      return typeof v === "string" && v.trim().length > 0;
    });
  }, [phase, data]);

  const setField = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleBack = () => setStep((s) => Math.max(0, s - 1));
  const handleNext = () => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));

  const handleGenerate = () => {
    setGenerating(true);
    // Simulate generation; in next prompt this will call an LLM.
    setTimeout(() => {
      navigate("/co-creator/result", { state: { wizardData: data } });
    }, 2000);
  };

  const runDemo = async () => {
    if (demoRunning) return;
    setDemoRunning(true);
    // 1. Clear existing wizard state
    try {
      localStorage.removeItem(WIZARD_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    let acc = {};
    // 2. Step 0 - Goal
    acc = { ...DEMO_STEP_DATA[0] };
    setData(acc);
    setStep(0);
    await sleep(800);
    // 3. Step 1 - Cat
    acc = { ...acc, ...DEMO_STEP_DATA[1] };
    setData(acc);
    setStep(1);
    await sleep(800);
    // 4. Step 2 - Owl
    acc = { ...acc, ...DEMO_STEP_DATA[2] };
    setData(acc);
    setStep(2);
    await sleep(800);
    // 5. Step 3 - Bee
    acc = { ...acc, ...DEMO_STEP_DATA[3] };
    setData(acc);
    setStep(3);
    await sleep(800);
    // 6. Step 4 - Dog
    acc = { ...acc, ...DEMO_STEP_DATA[4] };
    setData(acc);
    setStep(4);
    await sleep(800);
    // 7. Generate
    setGenerating(true);
    await sleep(2000);
    navigate("/co-creator/result", {
      state: { wizardData: DEMO_FULL_DATA, demo: true },
    });
  };

  if (generating) {
    return <GeneratingScreen />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="co-creator-page">
      <Header />

      {/* Demo CTA — visible only on step 0 before user starts typing */}
      {step === 0 && !demoRunning && (
        <div
          className="border-b border-slate-100 bg-gradient-to-b from-blue-50/40 to-white"
          data-testid="demo-cta-section"
        >
          <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10 sm:py-12 text-center">
            <button
              type="button"
              onClick={runDemo}
              data-testid="demo-cta-btn"
              className="demo-pulse inline-flex items-center justify-center gap-2.5 rounded-2xl bg-[#1E40AF] hover:bg-blue-900 text-white font-semibold px-7 py-5 text-base sm:text-lg transition-transform duration-200 hover:-translate-y-0.5"
            >
              <PlayCircle className="w-5 h-5" />
              Demo ausprobieren — „E-Mail an Team" ansehen
            </button>
            <p
              className="mt-4 text-sm text-slate-500"
              data-testid="demo-cta-helper"
            >
              Sieh den Co-Creator in 30 Sekunden — Use-Case wird automatisch
              geladen.
            </p>
          </div>
        </div>
      )}

      {/* Top progress */}
      <div className="border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-8 pb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${phase.accent.chip}`}
                data-testid="phase-chip"
              >
                <Sparkles className="w-3 h-3" />
                Co-Creator
              </span>
              <span
                className="text-sm font-bold text-slate-700"
                data-testid="phase-indicator"
              >
                Phase {step} von {TOTAL_STEPS}
              </span>
            </div>
            <span className="text-xs font-semibold text-slate-400 tabular-nums">
              {step + 1} / {TOTAL_STEPS}
            </span>
          </div>
          <div
            className="mt-3 h-1 w-full rounded-full bg-slate-100 overflow-hidden"
            data-testid="phase-progress"
          >
            <div
              className={`h-full ${phase.accent.bar} transition-all duration-500 ease-out`}
              style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
              data-testid="phase-progress-bar"
            />
          </div>
        </div>
      </div>

      {/* Step content */}
      <main className="flex-1 px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto" data-testid={`step-${step}`}>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 leading-tight"
            data-testid="step-heading"
          >
            {step === 0 ? phase.title : `Phase ${step} von ${TOTAL_STEPS} — ${phase.title}`}
          </h1>
          <p
            className="mt-4 text-lg text-slate-600 leading-relaxed max-w-2xl"
            data-testid="step-subheading"
          >
            {phase.subtitle}
          </p>

          <div className="mt-10 space-y-7">
            {phase.fields.map((field, idx) => (
              <FieldRenderer
                key={field.key}
                field={field}
                value={data[field.key] || ""}
                onChange={(v) => setField(field.key, v)}
                accentHex={phase.accent.hex}
                testIdSuffix={`${step}-${idx}`}
              />
            ))}
          </div>

          {/* Footer actions */}
          <div className="mt-12 flex items-center justify-between gap-3">
            {step > 0 ? (
              <Button
                onClick={handleBack}
                disabled={demoRunning}
                variant="outline"
                data-testid="wizard-back-btn"
                className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-50 font-semibold h-12 px-5"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Zurück
              </Button>
            ) : (
              <span />
            )}

            {step < TOTAL_STEPS - 1 ? (
              <Button
                onClick={handleNext}
                disabled={!isStepValid || demoRunning}
                data-testid="wizard-next-btn"
                className="rounded-xl bg-[#1E40AF] hover:bg-blue-900 text-white font-semibold h-12 px-7 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {demoRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Demo läuft
                  </>
                ) : (
                  <>
                    Weiter
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={!isStepValid || demoRunning}
                data-testid="wizard-generate-btn"
                className="rounded-xl bg-[#1E40AF] hover:bg-blue-900 text-white font-semibold h-12 px-7 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {demoRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Demo läuft
                  </>
                ) : (
                  <>
                    Nachricht generieren
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, accentHex, testIdSuffix }) {
  const labelEl = (
    <Label
      htmlFor={`field-${field.key}`}
      className="block text-base font-semibold text-slate-900 mb-2.5"
      data-testid={`field-label-${field.key}`}
    >
      {field.label}
    </Label>
  );

  const focusStyle = {
    // applied via inline style + CSS var trick is overkill; rely on ring color via Tailwind hex utility
  };

  if (field.kind === "input") {
    return (
      <div data-testid={`field-${testIdSuffix}`}>
        {labelEl}
        <Input
          id={`field-${field.key}`}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          data-testid={`field-input-${field.key}`}
          className="rounded-xl border-slate-200 h-12 text-base focus-visible:ring-2"
          style={{
            "--tw-ring-color": `${accentHex}40`,
            ...focusStyle,
          }}
        />
      </div>
    );
  }

  if (field.kind === "select") {
    return (
      <div data-testid={`field-${testIdSuffix}`}>
        {labelEl}
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger
            id={`field-${field.key}`}
            data-testid={`field-select-${field.key}`}
            className="rounded-xl border-slate-200 h-12 text-base"
          >
            <SelectValue placeholder={field.placeholder} />
          </SelectTrigger>
          <SelectContent
            className="rounded-xl"
            data-testid={`field-select-content-${field.key}`}
          >
            {field.options.map((opt) => (
              <SelectItem
                key={opt}
                value={opt}
                data-testid={`field-select-option-${field.key}-${opt
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // textarea
  return (
    <div data-testid={`field-${testIdSuffix}`}>
      {labelEl}
      <Textarea
        id={`field-${field.key}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        rows={field.rows || 3}
        data-testid={`field-textarea-${field.key}`}
        className="rounded-xl border-slate-200 text-base leading-relaxed resize-y min-h-[100px] focus-visible:ring-2"
        style={{ "--tw-ring-color": `${accentHex}40` }}
      />
    </div>
  );
}

function GeneratingScreen() {
  return (
    <div
      className="min-h-screen bg-white flex flex-col"
      data-testid="generating-screen"
    >
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="text-center max-w-md">
          <div className="relative w-20 h-20 mx-auto">
            <div className="absolute inset-0 rounded-full bg-blue-50 animate-pulse" />
            <div className="relative w-full h-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#1E40AF] animate-spin" />
            </div>
          </div>
          <h2
            className="mt-8 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900"
            data-testid="generating-headline"
          >
            Generierung läuft …
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Wir verweben Eule, Biene, Katze und Hund in deine Nachricht. Das
            dauert nur einen Moment.
          </p>
        </div>
      </main>
    </div>
  );
}
